import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { getBrowserPosition } from "@/lib/geo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Plus, MapPin, Loader2, Check, Navigation, User as UserIcon, GraduationCap, MapPinned, Users as UsersIcon, Sparkles, ShieldCheck, Stars, Camera, Linkedin, Instagram, Facebook, Twitter, Youtube, Globe, Lock, Crown, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Combobox from "@/components/umaang/Combobox";
import {
  RELIGIONS, EDUCATION, PROFESSIONS, DIETS, DIET_VALUES,
  MARITAL_STATUS, MOTHER_TONGUES, AGES, FAMILY_TYPES, FAMILY_VALUES,
  RASHIS, NAKSHATRAS, HOBBIES, CITIES, INCOME_BRACKETS, EMPLOYMENT_TYPES, lookupPincode,
} from "@/lib/refdata";

const FIELD = "border-[color:var(--umg-border)]";

function Section({ title, subtitle, children, icon: Icon, color = "#C85A53" }) {
  return (
    <div className="border p-6 relative overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
      <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10" style={{ background: color }} />
      <div className="relative flex items-start gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}18`, color }}>
            <Icon className="w-5 h-5" strokeWidth={1.8} />
          </div>
        )}
        <div>
          <h3 className="font-display text-2xl" style={{ color: Icon ? color : undefined }}>{title}</h3>
          {subtitle && <p className="text-xs text-[color:var(--umg-text-2)] mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-4 relative">{children}</div>
    </div>
  );
}

function Field({ label, hint, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label>{label}</Label>
      <div className="mt-2">{children}</div>
      {hint && <div className="text-[10px] text-[color:var(--umg-text-2)] mt-1">{hint}</div>}
    </div>
  );
}

function ChipList({ options, value = [], onChange, testId }) {
  const on = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = value.includes(o);
        return (
          <button
            type="button"
            key={o}
            data-testid={testId ? `${testId}-${o}` : undefined}
            onClick={() => on(o)}
            className={`text-xs px-2.5 py-1 rounded-full border transition ${
              active
                ? "bg-[#C85A53] text-white border-[#C85A53]"
                : "border-[color:var(--umg-border)] hover:bg-[color:var(--umg-secondary)]"
            }`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function EditProfile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [pinDone, setPinDone] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  // Social + visibility (Premium features)
  const [social, setSocial] = useState({ linkedin: "", facebook: "", instagram: "", twitter: "", youtube: "", website: "" });
  const [visibility, setVisibility] = useState({ photos: "everyone", contact: "accepted", social: "premium" });
  const [whitelistCounts, setWhitelistCounts] = useState({ photos: 0, contact: 0, social: 0 });
  const [addingWl, setAddingWl] = useState({ scope: "", memberId: "" });

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setForm(data);
      if (data.visibility) setVisibility({ ...visibility, ...data.visibility });
      api.get(`/profiles/${data.id}`).then(({ data: p }) => setPhotos(p.photos_visible || []));
    });
    // Load social + whitelist counts (may 402 for free users — fine)
    api.get("/profile/social").then(({ data }) => setSocial((s) => ({ ...s, ...(data.social_links || {}) }))).catch(() => {});
    api.get("/profile/whitelist").then(({ data }) => {
      const wl = data.whitelist || {};
      setWhitelistCounts({ photos: (wl.photos || []).length, contact: (wl.contact || []).length, social: (wl.social || []).length });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onPin = async (pin) => {
    setForm((f) => ({ ...f, pin_code: pin }));
    setPinDone(false);
    if (!/^\d{6}$/.test(pin)) return;
    setPinLoading(true);
    const res = await lookupPincode(pin);
    setPinLoading(false);
    if (res) {
      setForm((f) => ({ ...f, city: res.city, state: res.state, country: res.country }));
      setPinDone(true);
      toast.success(`PIN ${pin} → ${res.city}, ${res.state}`);
    } else {
      toast.error("PIN not found — please fill city/state manually");
    }
  };

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      if (photos.length >= 10) { toast.error("Max 10 photos"); break; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        setPhotos((prev) => [...prev, `${backend}${data.url}`]);
      } catch {
        try {
          const b64 = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
          setPhotos((prev) => [...prev, b64]);
        } catch { toast.error("Upload failed"); }
      }
    }
    e.target.value = "";
  };

  const removePhoto = (i) => setPhotos((prev) => prev.filter((_, idx) => idx !== i));

  const detectLocation = async () => {
    setGeoBusy(true);
    try {
      const p = await getBrowserPosition();
      if (!p) {
        toast.error("Location unavailable — permission denied or unsupported");
        return;
      }
      setForm((f) => ({ ...f, latitude: p.latitude, longitude: p.longitude }));
      await api.post("/profile/location", { latitude: p.latitude, longitude: p.longitude });
      toast.success("Location saved · Near Me radius active");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setGeoBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        age: form.age ? Number(form.age) : undefined,
        gender: form.gender,
        education: form.education,
        profession: form.profession,
        income: form.income,
        employment_type: form.employment_type,
        diet: form.diet,
        address: form.address,
        secondary_address: form.secondary_address,
        pin_code: form.pin_code,
        city: form.city,
        state: form.state,
        country: form.country,
        marital_status: form.marital_status,
        has_children: form.has_children,
        children_count: form.children_count ? Number(form.children_count) : undefined,
        religion: form.religion,
        caste: form.caste,
        mother_tongue: form.mother_tongue,
        hobbies: Array.isArray(form.hobbies) ? form.hobbies : (form.hobbies ? String(form.hobbies).split(",").map((s) => s.trim()).filter(Boolean) : undefined),
        family_size: form.family_size ? Number(form.family_size) : undefined,
        family_type: form.family_type,
        about: form.about,
        aadhaar: form.aadhaar,
        pan: form.pan,
        photos,
        prefer_age_min: form.prefer_age_min ? Number(form.prefer_age_min) : undefined,
        prefer_age_max: form.prefer_age_max ? Number(form.prefer_age_max) : undefined,
        prefer_diet: form.prefer_diet,
        prefer_city: form.prefer_city,
        prefer_marital_status: form.prefer_marital_status ? (Array.isArray(form.prefer_marital_status) ? form.prefer_marital_status : [form.prefer_marital_status]) : undefined,
        prefer_religion: form.prefer_religion,
        prefer_mother_tongue: form.prefer_mother_tongue,
        prefer_caste: form.prefer_caste,
        prefer_employment_type: form.prefer_employment_type,
        prefer_profession: form.prefer_profession,
        prefer_income: form.prefer_income,
        prefer_education: form.prefer_education,
        dob: form.dob,
        birth_time: form.birth_time,
        birth_place: form.birth_place,
        rashi: form.rashi,
        nakshatra: form.nakshatra,
        latitude: form.latitude ?? undefined,
        longitude: form.longitude ?? undefined,
        hide_name: !!form.hide_name,
      };
      await api.put("/profile", payload);
      // Save social links (Premium-gated — swallow 402 for free users)
      if (user?.is_premium || user?.role === "admin") {
        try { await api.put("/profile/social", social); } catch (e) { /* non-fatal */ }
      }
      // Save visibility (Premium-modes gated server-side)
      try { await api.put("/profile/visibility", visibility); } catch (e) { /* non-fatal */ }
      const fresh = await refresh();
      toast.success("Profile saved");
      // After save, take user to their profile view (with an Edit button) so
      // the edit form doesn't keep re-opening on every navigation.
      const uid = fresh?.id || user?.id;
      if (uid) nav(`/profile/${uid}`);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">Your presence</div>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Craft your profile</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[color:var(--umg-text-2)]">
            {user?.member_id && (
              <span data-testid="my-member-id" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-[#EEF2FF] to-[#F0FDF4] border border-[color:var(--umg-border)]">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--umg-text-2)]">Member ID</span>
                <code className="text-[#7C3AED] font-semibold">{user.member_id}</code>
              </span>
            )}
            <span>Referral: <code className="text-[#C85A53]">{user?.referral_code}</code></span>
          </div>
        </div>
      </div>

      {/* Privacy card — hide name toggle */}
      <div className="border p-5 relative overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-10 bg-[#7C3AED]" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-[#7C3AED18] text-[#7C3AED]">
              <UserIcon className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display text-lg">Hide my real name</div>
              <div className="text-xs text-[color:var(--umg-text-2)] mt-0.5">
                Show only your Member ID <code className="text-[#7C3AED] font-semibold">{user?.member_id || "USG…"}</code> to other members. You'll still appear as <b>{user?.name}</b> to yourself and admins.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch data-testid="edit-hide-name" checked={!!form.hide_name} onCheckedChange={(v) => setForm({ ...form, hide_name: v })} />
            <span className="text-sm text-[color:var(--umg-text-2)]">{form.hide_name ? "Hidden" : "Visible"}</span>
          </div>
        </div>
      </div>

      <div className="border p-6 relative overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }}>
        <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10 bg-[#0EA5E9]" />
        <div className="relative flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0 bg-[#0EA5E918] text-[#0EA5E9]">
            <Camera className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <h3 className="font-display text-2xl text-[#0EA5E9]">Photos <span className="text-sm text-[color:var(--umg-text-2)]">({photos.length}/10)</span></h3>
        </div>
        <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 gap-3">
          {photos.map((src, i) => (
            <div key={`${src}-${i}`} className="relative aspect-square overflow-hidden border" style={{ borderRadius: 6, borderColor: "var(--umg-border)" }}>
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button data-testid={`remove-photo-${i}`} onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {photos.length < 10 && (
            <label data-testid="add-photo-btn" className="aspect-square border border-dashed hover:bg-[color:var(--umg-secondary)] cursor-pointer grid place-items-center transition" style={{ borderRadius: 6, borderColor: "var(--umg-border)", background: "var(--umg-bg)" }}>
              <input type="file" accept="image/*" multiple hidden onChange={addPhoto} data-testid="photo-input" />
              <Plus className="w-6 h-6 text-[color:var(--umg-text-2)]" />
            </label>
          )}
        </div>
      </div>

      <Section title="Basic details" icon={UserIcon} color="#C85A53">
        <Field label="Name"><Input data-testid="edit-name" className={FIELD} value={form.name || ""} onChange={set("name")} /></Field>
        <Field label="Age">
          <Combobox
            testId="edit-age"
            value={form.age ? String(form.age) : ""}
            onChange={(v) => setForm({ ...form, age: Number(v) })}
            options={AGES.map((a) => ({ value: String(a), label: `${a} yrs` }))}
            placeholder="Select age"
          />
        </Field>
        <Field label="Gender">
          <Select value={form.gender || ""} onValueChange={(v) => setForm({ ...form, gender: v })}>
            <SelectTrigger data-testid="edit-gender" className={FIELD}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Marital status">
          <Select value={form.marital_status || ""} onValueChange={(v) => setForm({ ...form, marital_status: v })}>
            <SelectTrigger data-testid="edit-marital" className={FIELD}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {MARITAL_STATUS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Mother tongue">
          <Combobox testId="edit-tongue" value={form.mother_tongue || ""} onChange={(v) => setForm({ ...form, mother_tongue: v })} options={MOTHER_TONGUES} placeholder="Select language" />
        </Field>
        <Field label="Religion">
          <Combobox testId="edit-religion" value={form.religion || ""} onChange={(v) => setForm({ ...form, religion: v, caste: "" })} options={RELIGIONS} placeholder="Select religion" />
        </Field>
        <Field label="Caste / Community" hint="Type your community — leave blank if you prefer not to say">
          <Input data-testid="edit-caste" className={FIELD} value={form.caste || ""} onChange={set("caste")} placeholder="e.g. Agarwal, Brahmin, Reddy…" />
        </Field>
        <Field label="Diet">
          <Combobox
            testId="edit-diet"
            value={form.diet || ""}
            onChange={(v) => setForm({ ...form, diet: v })}
            options={DIETS.map((d) => ({ value: DIET_VALUES[d] || d, label: d }))}
            placeholder="Select diet"
          />
        </Field>
      </Section>

      <Section title="Education & Profession" icon={GraduationCap} color="#2563EB">
        <Field label="Highest qualification">
          <Combobox testId="edit-education" value={form.education || ""} onChange={(v) => setForm({ ...form, education: v })} options={EDUCATION} placeholder="Select qualification" />
        </Field>
        <Field label="Employment type / Job">
          <Combobox testId="edit-employment-type" value={form.employment_type || ""} onChange={(v) => setForm({ ...form, employment_type: v })} options={EMPLOYMENT_TYPES} placeholder="Select employment type" />
        </Field>
        <Field label="Profession">
          <Combobox testId="edit-profession" value={form.profession || ""} onChange={(v) => setForm({ ...form, profession: v })} options={PROFESSIONS} placeholder="Select profession" />
        </Field>
        <Field label="Annual income">
          <Combobox testId="edit-income" value={form.income || ""} onChange={(v) => setForm({ ...form, income: v })} options={INCOME_BRACKETS} placeholder="Select income range" />
        </Field>
      </Section>

      <Section title="Address" icon={MapPinned} color="#059669" subtitle="Enter your 6-digit PIN — city & state will auto-fill.">
        <Field label="PIN code" hint={pinDone ? "✓ Auto-filled from India Post" : "6-digit postal code"}>
          <div className="relative">
            <Input
              data-testid="edit-pin"
              inputMode="numeric"
              maxLength={6}
              value={form.pin_code || ""}
              onChange={(e) => onPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className={FIELD}
              placeholder="e.g. 302001"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {pinLoading ? <Loader2 className="w-4 h-4 animate-spin text-[color:var(--umg-text-2)]" />
                : pinDone ? <Check className="w-4 h-4 text-[#4F7942]" />
                : <MapPin className="w-4 h-4 text-[color:var(--umg-text-2)]" />}
            </div>
          </div>
        </Field>
        <Field label="Country"><Input className={FIELD} value={form.country || "India"} onChange={set("country")} /></Field>
        <Field label="City">
          <Combobox
            testId="edit-city"
            value={form.city || ""}
            onChange={(v) => setForm({ ...form, city: v })}
            options={form.city && !CITIES.includes(form.city) ? [form.city, ...CITIES] : CITIES}
            placeholder="Select city"
          />
        </Field>
        <Field label="State"><Input data-testid="edit-state" className={FIELD} value={form.state || ""} onChange={set("state")} /></Field>
        <Field label="Precise location · Near Me radius" hint={form.latitude && form.longitude ? `Saved · ${Number(form.latitude).toFixed(4)}, ${Number(form.longitude).toFixed(4)}` : "Enables the 100km 'Near Me' feed. Denying is fine — we'll fall back to city."} wide>
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              type="button"
              data-testid="detect-location-btn"
              disabled={geoBusy}
              onClick={detectLocation}
              className="btn-pill bg-white hover:bg-[color:var(--umg-secondary)] text-[color:var(--umg-text)] border border-[color:var(--umg-border)]"
            >
              {geoBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Navigation className="w-4 h-4 mr-1.5" />}
              {form.latitude && form.longitude ? "Refresh location" : "Detect my location"}
            </Button>
            {form.latitude && form.longitude && (
              <span data-testid="location-badge" className="inline-flex items-center gap-1.5 text-xs text-[#4F7942]">
                <Check className="w-3.5 h-3.5" /> Location on
              </span>
            )}
          </div>
        </Field>
        <Field label="Address (optional)" wide>
          <Textarea data-testid="edit-address" rows={2} className={FIELD} value={form.address || ""} onChange={set("address")} placeholder="Street, area, landmark" />
        </Field>
        <Field label="Second address (optional)" hint="Agar aap kabhi kabhi kahin aur rehte ho — ek aur ghar / office / hometown address" wide>
          <Textarea
            data-testid="edit-secondary-address"
            rows={2}
            className={FIELD}
            value={form.secondary_address || ""}
            onChange={set("secondary_address")}
            placeholder="Alternate address — city, state or full address"
          />
        </Field>
      </Section>

      <Section title="Family" icon={UsersIcon} color="#B45309">
        <Field label="Family type">
          <Combobox
            testId="edit-family-type"
            value={form.family_type || ""}
            onChange={(v) => setForm({ ...form, family_type: v })}
            options={FAMILY_TYPES.map((f) => ({ value: FAMILY_VALUES[f] || f.toLowerCase(), label: f }))}
            placeholder="Select"
          />
        </Field>
        <Field label="Family size">
          <Input type="number" min={1} max={30} className={FIELD} value={form.family_size || ""} onChange={set("family_size")} />
        </Field>
        <Field label="Do you have children?">
          <div className="flex items-center gap-3 pt-2">
            <Switch data-testid="edit-has-children" checked={!!form.has_children} onCheckedChange={(v) => setForm({ ...form, has_children: v })} />
            <span className="text-sm text-[color:var(--umg-text-2)]">{form.has_children ? "Yes" : "No"}</span>
          </div>
        </Field>
        {form.has_children && (
          <Field label="Children count"><Input type="number" min={1} max={12} className={FIELD} value={form.children_count || ""} onChange={set("children_count")} /></Field>
        )}
      </Section>

      <Section title="About & interests" icon={Sparkles} color="#DB2777">
        <Field label="About me" wide>
          <Textarea data-testid="edit-about" rows={4} className={FIELD} value={form.about || ""} onChange={set("about")} placeholder="A few lines about yourself…" />
        </Field>
        <Field label="Hobbies (pick any)" wide>
          <ChipList options={HOBBIES} value={Array.isArray(form.hobbies) ? form.hobbies : (form.hobbies ? String(form.hobbies).split(",").map((s) => s.trim()) : [])} onChange={(v) => setForm({ ...form, hobbies: v })} testId="hobby" />
        </Field>
      </Section>

      <Section title="Partner preferences" icon={Sparkles} color="#7C3AED">
        <Field label="Preferred age (min)">
          <Combobox testId="pref-age-min" value={form.prefer_age_min ? String(form.prefer_age_min) : ""} onChange={(v) => setForm({ ...form, prefer_age_min: Number(v) })} options={AGES.map((a) => ({ value: String(a), label: `${a} yrs` }))} placeholder="Min age" />
        </Field>
        <Field label="Preferred age (max)">
          <Combobox testId="pref-age-max" value={form.prefer_age_max ? String(form.prefer_age_max) : ""} onChange={(v) => setForm({ ...form, prefer_age_max: Number(v) })} options={AGES.map((a) => ({ value: String(a), label: `${a} yrs` }))} placeholder="Max age" />
        </Field>
        <Field label="Preferred mother tongue">
          <Combobox testId="pref-mother-tongue" value={form.prefer_mother_tongue || ""} onChange={(v) => setForm({ ...form, prefer_mother_tongue: v })} options={MOTHER_TONGUES} placeholder="Select language" />
        </Field>
        <Field label="Preferred religion">
          <Combobox testId="pref-religion" value={form.prefer_religion || ""} onChange={(v) => setForm({ ...form, prefer_religion: v, prefer_caste: "" })} options={RELIGIONS} placeholder="Select religion" />
        </Field>
        <Field label="Preferred caste / community" hint="Type preferred community, or leave blank for no preference">
          <Input data-testid="pref-caste" className={FIELD} value={form.prefer_caste || ""} onChange={set("prefer_caste")} placeholder="e.g. Agarwal, Brahmin, Reddy…" />
        </Field>
        <Field label="Preferred diet">
          <Combobox testId="pref-diet" value={form.prefer_diet || ""} onChange={(v) => setForm({ ...form, prefer_diet: v })} options={DIETS.map((d) => ({ value: DIET_VALUES[d] || d, label: d }))} placeholder="Select diet" />
        </Field>
        <Field label="Preferred employment type / Job">
          <Combobox testId="pref-employment-type" value={form.prefer_employment_type || ""} onChange={(v) => setForm({ ...form, prefer_employment_type: v })} options={EMPLOYMENT_TYPES} placeholder="Select employment type" />
        </Field>
        <Field label="Preferred profession">
          <Combobox testId="pref-profession" value={form.prefer_profession || ""} onChange={(v) => setForm({ ...form, prefer_profession: v })} options={PROFESSIONS} placeholder="Select profession" />
        </Field>
        <Field label="Preferred annual income">
          <Combobox testId="pref-income" value={form.prefer_income || ""} onChange={(v) => setForm({ ...form, prefer_income: v })} options={INCOME_BRACKETS} placeholder="Select income range" />
        </Field>
        <Field label="Preferred highest qualification">
          <Combobox testId="pref-education" value={form.prefer_education || ""} onChange={(v) => setForm({ ...form, prefer_education: v })} options={EDUCATION} placeholder="Select qualification" />
        </Field>
        <Field label="Preferred cities" hint={`Select up to 6 (${(Array.isArray(form.prefer_city) ? form.prefer_city.length : (form.prefer_city ? 1 : 0))}/6 selected)`} wide>
          <div className="flex flex-wrap gap-2 mb-2">
            {(Array.isArray(form.prefer_city) ? form.prefer_city : (form.prefer_city ? [form.prefer_city] : [])).map((c) => (
              <span key={c} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#EFE7DC] text-[#7C3AED] font-semibold">
                {c}
                <button
                  type="button"
                  data-testid={`pref-city-remove-${c}`}
                  onClick={() => {
                    const current = Array.isArray(form.prefer_city) ? form.prefer_city : (form.prefer_city ? [form.prefer_city] : []);
                    setForm({ ...form, prefer_city: current.filter((x) => x !== c) });
                  }}
                  className="ml-0.5 text-[#7C3AED] hover:text-black"
                  aria-label={`Remove ${c}`}
                >×</button>
              </span>
            ))}
          </div>
          <Combobox
            testId="pref-city"
            value=""
            onChange={(v) => {
              if (!v) return;
              const current = Array.isArray(form.prefer_city) ? form.prefer_city : (form.prefer_city ? [form.prefer_city] : []);
              if (current.includes(v)) { toast(`${v} already selected`); return; }
              if (current.length >= 6) { toast.error("Maximum 6 cities allowed"); return; }
              setForm({ ...form, prefer_city: [...current, v] });
            }}
            options={CITIES}
            placeholder="Add a preferred city"
          />
        </Field>
        <Field label="Preferred marital status">
          <Select value={(form.prefer_marital_status || [])[0] || ""} onValueChange={(v) => setForm({ ...form, prefer_marital_status: [v] })}>
            <SelectTrigger className={FIELD}><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              {MARITAL_STATUS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </Section>

      {/* KYC section hidden from users — verification is currently paused. Backend + admin queue remain intact. */}
      {false && (
      <Section title="KYC (private)" icon={ShieldCheck} color="#4F7942" subtitle="Only Aadhaar last 4 digits are stored, never the full number.">
        <Field label="Aadhaar number"><Input data-testid="edit-aadhaar" className={FIELD} value={form.aadhaar || ""} onChange={set("aadhaar")} placeholder="XXXX XXXX XXXX" /></Field>
        <Field label="PAN number"><Input data-testid="edit-pan" className={FIELD} value={form.pan || ""} onChange={set("pan")} placeholder="ABCDE1234F" /></Field>
        <div className="sm:col-span-2 mt-2 p-4 border" style={{ borderRadius: 6, borderColor: "var(--umg-border)", background: "var(--umg-secondary)" }}>
          {user?.kyc_verified ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[#4F7942]">✓</span>
              <span>KYC Verified {form.aadhaar_last4 ? `· Aadhaar •••• ${form.aadhaar_last4}` : ""} {form.pan ? `· PAN ${form.pan}` : ""}</span>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <div className="text-sm font-medium">Instant DigiLocker verification</div>
                <div className="text-xs text-[color:var(--umg-text-2)] mt-0.5">Uses Digio · your Aadhaar stays with the government.</div>
              </div>
              <Button
                data-testid="start-kyc-btn"
                type="button"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/kyc/start", { aadhaar: form.aadhaar, pan: form.pan });
                    if (data.mocked) { toast.success("KYC verified (mocked)."); await refresh(); }
                    else if (data.redirect_url) { toast("Opening DigiLocker…"); window.location.href = data.redirect_url; }
                    else { toast("KYC request created. Please complete verification."); }
                  } catch (e) { toast.error(formatError(e)); }
                }}
                className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white"
              >
                Verify now
              </Button>
            </div>
          )}
        </div>
      </Section>
      )}

      <Section title="Horoscope (optional)" icon={Stars} color="#F59E0B">
        <Field label="Date of birth"><Input data-testid="edit-dob" type="date" className={FIELD} value={form.dob || ""} onChange={set("dob")} /></Field>
        <Field label="Birth time"><Input data-testid="edit-birth-time" type="time" className={FIELD} value={form.birth_time || ""} onChange={set("birth_time")} /></Field>
        <Field label="Birth place"><Input className={FIELD} value={form.birth_place || ""} onChange={set("birth_place")} /></Field>
        <Field label="Rashi (Moon sign)">
          <Combobox testId="edit-rashi" value={form.rashi || ""} onChange={(v) => setForm({ ...form, rashi: v })} options={RASHIS.map((r) => ({ value: r.split(" ")[0], label: r }))} placeholder="Select" />
        </Field>
        <Field label="Nakshatra">
          <Combobox testId="edit-nakshatra" value={form.nakshatra || ""} onChange={(v) => setForm({ ...form, nakshatra: v })} options={NAKSHATRAS} placeholder="Select" />
        </Field>
      </Section>

      <SocialLinksSection user={user} social={social} setSocial={setSocial} />
      <VisibilitySection
        user={user}
        visibility={visibility}
        setVisibility={setVisibility}
        whitelistCounts={whitelistCounts}
        setWhitelistCounts={setWhitelistCounts}
        addingWl={addingWl}
        setAddingWl={setAddingWl}
      />
      {/* Hide-profile section moved to /insights — single source of truth */}

      <div className="flex justify-end">
        <Button data-testid="save-profile-btn" disabled={saving} onClick={save} className="btn-pill bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-11 px-8 shadow-md shadow-[#C85A53]/30">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </div>
  );
}

// --------------- Social Links Section (Premium-gated) ---------------
const SOCIAL_ROWS = [
  { key: "linkedin",  icon: Linkedin,  label: "LinkedIn",  color: "#0A66C2", ph: "linkedin.com/in/your-handle" },
  { key: "instagram", icon: Instagram, label: "Instagram", color: "#E1306C", ph: "instagram.com/your-handle" },
  { key: "facebook",  icon: Facebook,  label: "Facebook",  color: "#1877F2", ph: "facebook.com/your.profile" },
  { key: "twitter",   icon: Twitter,   label: "X (Twitter)", color: "#0F172A", ph: "x.com/your-handle" },
  { key: "youtube",   icon: Youtube,   label: "YouTube",   color: "#FF0000", ph: "youtube.com/@your-channel" },
  { key: "website",   icon: Globe,     label: "Personal website", color: "#7C3AED", ph: "your-site.com" },
];

function SocialLinksSection({ user, social, setSocial }) {
  const isPremium = !!user?.is_premium || user?.role === "admin";
  return (
    <div className="relative border p-6 overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }} data-testid="social-links-section">
      <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10" style={{ background: "#0A66C2" }} />
      <div className="relative flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#0A66C218", color: "#0A66C2" }}>
          <Linkedin className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-2xl" style={{ color: "#0A66C2" }}>Social &amp; Professional</h3>
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-white px-2 py-0.5 rounded-full">
              <Crown className="w-3 h-3" /> Premium
            </span>
          </div>
          <p className="text-xs text-[color:var(--umg-text-2)] mt-1">Verify your profession & background — only Premium members can see these links (per your visibility rule).</p>
        </div>
      </div>
      {!isPremium ? (
        <div className="mt-5 p-5 border-2 border-dashed rounded-xl flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: "#F59E0B44", background: "#FEF3C7" }}>
          <div className="flex items-center gap-3">
            <Lock className="w-6 h-6 text-[#B45309]" />
            <div>
              <div className="font-semibold text-[#78350F]">Unlock Social &amp; Professional Links</div>
              <div className="text-xs text-[#92400E]">Upgrade to Premium to add LinkedIn, Instagram, and more.</div>
            </div>
          </div>
          <a href="/premium" data-testid="social-upgrade-cta" className="btn-pill bg-[#C85A53] hover:bg-[#A94842] text-white px-5 py-2 rounded-full text-sm">Upgrade →</a>
        </div>
      ) : (
        <div className="mt-5 grid sm:grid-cols-2 gap-4 relative">
          {SOCIAL_ROWS.map(({ key, icon: Icon, label, color, ph }) => (
            <div key={key} className="space-y-1.5">
              <label className="text-xs text-[color:var(--umg-text-2)] flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color }} /> {label}
              </label>
              <Input
                data-testid={`social-${key}`}
                className={FIELD}
                placeholder={ph}
                value={social[key] || ""}
                onChange={(e) => setSocial((s) => ({ ...s, [key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --------------- Visibility Controls Section ---------------
const VIS_OPTIONS = [
  { value: "everyone", label: "🌍 Everyone", desc: "Anyone browsing Umaang can see." },
  { value: "premium",  label: "💎 Premium members only", desc: "Free members see a locked badge." },
  { value: "accepted", label: "✅ Only my accepted contacts", desc: "Only those whose request I accepted." },
  { value: "custom",   label: "🎯 Custom — people I choose", desc: "Add specific Member IDs (USG####)." },
];
const VIS_SCOPES = [
  { key: "photos",  label: "Photos",          desc: "Who sees your full photo album beyond the first preview." },
  { key: "contact", label: "Contact details", desc: "Who sees your email & mobile number." },
  { key: "social",  label: "Social profiles", desc: "Who sees your LinkedIn / Instagram / other links." },
];

function VisibilitySection({ user, visibility, setVisibility, whitelistCounts, setWhitelistCounts, addingWl, setAddingWl }) {
  const isPremium = !!user?.is_premium || user?.role === "admin";
  const addToWhitelist = async (scope) => {
    const mid = (addingWl.memberId || "").trim().toUpperCase();
    if (!mid) return;
    try {
      const { data } = await api.post("/profile/whitelist", { scope, member_id: mid });
      setWhitelistCounts((c) => ({ ...c, [scope]: data.count }));
      setAddingWl({ scope: "", memberId: "" });
      toast.success(`Added ${mid} to ${scope} whitelist`);
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="relative border p-6 overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }} data-testid="visibility-section">
      <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10" style={{ background: "#7C3AED" }} />
      <div className="relative flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#7C3AED18", color: "#7C3AED" }}>
          <Lock className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div>
          <h3 className="font-display text-2xl" style={{ color: "#7C3AED" }}>Privacy — Who sees what</h3>
          <p className="text-xs text-[color:var(--umg-text-2)] mt-1">Fine-tune who can see your photos, contact details, and social profiles.</p>
        </div>
      </div>
      <div className="mt-5 space-y-5 relative">
        {VIS_SCOPES.map(({ key, label, desc }) => {
          const val = visibility[key] || "everyone";
          return (
            <div key={key} className="p-4 border rounded-xl" style={{ borderColor: "var(--umg-border)" }} data-testid={`visibility-${key}-row`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="text-[11px] text-[color:var(--umg-text-2)] mt-0.5">{desc}</div>
                </div>
                <Select value={val} onValueChange={(v) => setVisibility((prev) => ({ ...prev, [key]: v }))}>
                  <SelectTrigger data-testid={`visibility-${key}-select`} className={`${FIELD} w-full sm:w-72`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VIS_OPTIONS.map((o) => {
                      const isPremiumOnly = o.value === "premium" || o.value === "custom";
                      return (
                        <SelectItem
                          key={o.value}
                          value={o.value}
                          disabled={isPremiumOnly && !isPremium}
                        >
                          <div className="flex items-center gap-2">
                            {o.label}
                            {isPremiumOnly && !isPremium && <Lock className="w-3 h-3 text-[#B45309]" />}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {val === "custom" && (
                <div className="mt-3 p-3 bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg">
                  <div className="text-xs text-[#5B21B6] mb-2">
                    <b>{whitelistCounts[key] || 0}</b> {whitelistCounts[key] === 1 ? "person" : "people"} in this list
                  </div>
                  <div className="flex gap-2">
                    <Input
                      data-testid={`wl-input-${key}`}
                      placeholder="Add Member ID e.g. USG1234"
                      className={FIELD}
                      value={addingWl.scope === key ? addingWl.memberId : ""}
                      onChange={(e) => setAddingWl({ scope: key, memberId: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addToWhitelist(key); } }}
                    />
                    <Button
                      data-testid={`wl-add-${key}`}
                      type="button"
                      onClick={() => addToWhitelist(key)}
                      className="btn-pill bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --------------- Hide Profile Section ---------------
const HIDE_OPTIONS = [
  { key: "10d", label: "10 days",  days: 10, tone: "#059669" },
  { key: "20d", label: "20 days",  days: 20, tone: "#0891B2" },
  { key: "1m",  label: "1 month",  days: 30, tone: "#7C3AED" },
  { key: "3m",  label: "3 months", days: 90, tone: "#DB2777" },
];

function HideProfileSection() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = async () => {
    try { const { data } = await api.get("/profile/hide-status"); setStatus(data); }
    catch (e) { /* ignore */ }
  };
  React.useEffect(() => { load(); }, []);
  const hide = async (key) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/profile/hide", { duration: key });
      toast.success("Profile hidden — you can unhide anytime");
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  const unhide = async () => {
    setBusy(true);
    try { await api.post("/profile/unhide"); toast.success("Profile is visible again"); await load(); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative border p-6 overflow-hidden" style={{ borderRadius: 12, borderColor: "var(--umg-border)", background: "var(--umg-surface)" }} data-testid="hide-profile-section">
      <div className="absolute -top-14 -right-14 w-40 h-40 rounded-full opacity-10" style={{ background: "#F59E0B" }} />
      <div className="relative flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: "#FEF3C7", color: "#B45309" }}>
          <EyeOff className="w-5 h-5" strokeWidth={1.8} />
        </div>
        <div>
          <h3 className="font-display text-2xl" style={{ color: "#B45309" }}>Hide profile temporarily</h3>
          <p className="text-xs text-[color:var(--umg-text-2)] mt-1">Take a break — your profile will not appear in searches or match results until you unhide it or the duration ends.</p>
        </div>
      </div>
      {status?.hidden ? (
        <div className="mt-5 p-4 rounded-xl border-2 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ borderColor: "#FDE68A", background: "#FFFBEB" }}>
          <div className="text-sm text-[#78350F]">
            <div className="font-semibold">Currently hidden</div>
            <div className="text-xs">Auto-unhide in <b>{status.days_remaining} days</b> ({new Date(status.hidden_until).toLocaleDateString()})</div>
          </div>
          <Button data-testid="unhide-btn" onClick={unhide} disabled={busy} className="btn-pill bg-[#B45309] hover:bg-[#92400E] text-white">
            Unhide now
          </Button>
        </div>
      ) : (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {HIDE_OPTIONS.map((o) => (
            <button
              key={o.key}
              data-testid={`hide-${o.key}`}
              onClick={() => hide(o.key)}
              disabled={busy}
              className="p-4 border-2 rounded-xl text-center transition hover:shadow-sm disabled:opacity-50"
              style={{ borderColor: `${o.tone}44`, background: `${o.tone}0d`, color: o.tone }}
            >
              <div className="font-display text-lg">{o.label}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-70 mt-0.5">{o.days} din</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

